import { Form, Input, InputNumber, message, Modal, Select } from "antd";
import { getUdfComponent } from "common/utils";
import { useFetchDebtorAccountsQuery } from "features/debtorAccountsTable/agencyPortal/debtorAccountsAPI";
import {
  useAddDebtorAssetMutation,
  useUpdateDebtorAssetMutation,
} from "features/debtorAssets/debtorAssetsAPI";
import { useFetchDebtorAssetCategoriesQuery } from "features/debtorAssetCategories/debtorAssetCategoriesAPI";
import { useFetchAllDebtorAssetCustomFieldsQuery } from "features/userDefinedFields/debtorAssetCustomFieldsAPI";
import { useMemo } from "react";
import styled from "styled-components";

const StyledForm = styled(Form)`
  margin-top: 12px;
  max-width: 400px;
`;

export default function AddDebtorAssetModal({ debtorId, asset, open, onOk, onCancel }) {
  const [form] = Form.useForm();
  const [addAsset, { isLoading: isAdding }] = useAddDebtorAssetMutation();
  const [updateAsset, { isLoading: isUpdating }] = useUpdateDebtorAssetMutation();
  const { data: categories, isLoading: categoriesLoading } = useFetchDebtorAssetCategoriesQuery();
  const { data: customFields = [] } = useFetchAllDebtorAssetCustomFieldsQuery();
  const { data: debtorAccounts, isLoading: accountsLoading } = useFetchDebtorAccountsQuery({
    debtorId,
  });

  const customFieldIdToObjectMap = useMemo(() => {
    return customFields.reduce((acc, field) => {
      acc[field.id] = field;
      return acc;
    }, {});
  }, [customFields]);

  const isNew = !asset;
  const isLoading = isAdding || isUpdating;

  // Get selected category to determine which custom fields to show
  const selectedCategoryId = Form.useWatch("debtorAssetCategoryId", form);

  // Get custom fields for selected category
  const selectedCategory = useMemo(() => {
    return categories?.find((cat) => cat.id === selectedCategoryId);
  }, [categories, selectedCategoryId]);

  const customFieldsForCategory = useMemo(() => {
    const fieldIds = selectedCategory?.debtorAssetCustomFieldIds || [];
    return fieldIds.map((id) => customFieldIdToObjectMap[id]).filter(Boolean);
  }, [selectedCategory, customFieldIdToObjectMap]);

  // Build category options
  const categoryOptions = useMemo(() => {
    return (
      categories
        ?.filter((cat) => !cat.isArchived)
        ?.map((cat) => ({
          label: cat.name,
          value: cat.id,
        })) || []
    );
  }, [categories]);

  // Build account options (only debtor's accounts)
  const accountOptions = useMemo(() => {
    return (
      debtorAccounts?.map((account) => ({
        label: `${account.externalId} - ${account.debtorName || "Unknown Client"}`,
        value: account.id,
      })) || []
    );
  }, [debtorAccounts]);

  const handleSubmit = async () => {
    const values = await form.validateFields();

    // Build custom field values from form data
    const customFieldValues = {};
    customFieldsForCategory.forEach((field) => {
      const value = values.customFields?.[field.slug];
      if (value !== undefined && value !== null && value !== "") {
        customFieldValues[field.slug] = value;
      }
    });

    const apiData = {
      debtorId,
      debtorAssetCategoryId: values.debtorAssetCategoryId,
      name: values.name,
      value: values.value || null,
      accountIds: values.accountIds || [],
      customFieldValues: Object.keys(customFieldValues).length > 0 ? customFieldValues : null,
    };

    let response;
    if (asset) {
      response = await updateAsset({
        ...apiData,
        debtorAssetId: asset.id,
      });
    } else {
      response = await addAsset(apiData);
    }

    if ("data" in response) {
      message.success(`Asset ${asset ? "updated" : "created"} successfully`);
      onOk();
    }
  };

  const initialValues = asset
    ? {
        debtorAssetCategoryId: asset.debtorAssetCategoryId,
        name: asset.name,
        value: asset.value,
        accountIds: asset.accountIds || [],
        customFields: asset.customFieldValues || {},
      }
    : { accountIds: [] };

  return (
    <Modal
      title={isNew ? "Add Asset" : "Edit Asset"}
      open={open}
      onOk={handleSubmit}
      onCancel={onCancel}
      okButtonProps={{ loading: isLoading }}
      maskClosable={false}
    >
      <StyledForm form={form} layout="vertical" initialValues={initialValues}>
        <Form.Item
          name="debtorAssetCategoryId"
          label="Asset Category"
          rules={[{ required: true, message: "Please select an asset category" }]}
        >
          <Select
            showSearch
            allowClear
            popupMatchSelectWidth={false}
            loading={categoriesLoading}
            placeholder="Select asset category"
            options={categoryOptions}
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
          />
        </Form.Item>

        <Form.Item
          name="name"
          label="Asset Name"
          rules={[{ required: true, message: "Please enter asset name" }]}
        >
          <Input placeholder="Enter asset name (e.g., 2020 Honda Civic)" />
        </Form.Item>

        <Form.Item name="value" label="Value">
          <InputNumber
            placeholder="Enter estimated value"
            min={0}
            precision={2}
            formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
            parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
          />
        </Form.Item>

        <Form.Item
          name="accountIds"
          label="Accounts"
          tooltip="Select accounts to associate with this asset"
        >
          <Select
            mode="multiple"
            allowClear
            showSearch
            popupMatchSelectWidth={false}
            maxTagTextLength={20}
            loading={accountsLoading}
            placeholder="Select accounts"
            options={accountOptions}
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
          />
        </Form.Item>

        {customFieldsForCategory.map((field) => (
          <Form.Item
            key={field.id}
            label={field.name}
            name={["customFields", field.slug]}
            rules={[{ required: field.isRequired, message: `Please enter ${field.name}` }]}
          >
            {getUdfComponent(field)}
          </Form.Item>
        ))}
      </StyledForm>
    </Modal>
  );
}
